import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    HostBinding,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { LegendButtons } from '@se/cp.common';
import { IColorCode } from '../models/IColorCode';
import { LegendBtnConfig, LegendButtonsConf } from './LegendButtons';
import { LegendClickEvent } from './legendClickEvent';

@Component({
    selector: 'map-legend',
    templateUrl: './legend.component.html',
    styleUrls: ['./legend.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LegendComponent implements OnChanges {

    public internalSelected: IColorCode;

    @Input()
    public legend: IColorCode[];

    @Input()
    public actualColor: IColorCode;

    @Input()
    public isHorizontal = false;

    @Input()
    public buttonsConfiguration: LegendButtonsConf;

    @Input()
    public readOnly: boolean = false;

    public get accentButtonColor() {
        return this.actualColor ? this.actualColor.color : '';
    }

    public parsedButtonsConfiguration: LegendBtnConfig[];

    @Output()
    public colorChanged = new EventEmitter<IColorCode>();

    @Output()
    public buttonClicked = new EventEmitter<LegendClickEvent>();

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.buttonsConfiguration && !changes.actualColor) {
            this.parsedButtonsConfiguration = this.getButtonsConfiguration();
        }
        if (changes.readOnly || changes.actualColor) {
            if (!this.readOnly) {
                this.internalSelected =this.actualColor;
                this.parsedButtonsConfiguration = this.getButtonsConfiguration();
            } else {
                this.internalSelected = null;
            };
        }

    }

    @HostBinding('class')
    get HorizontalClass() {
        return this.isHorizontal ? 'horizontal-legend' : 'vertical-legend';
    }

    public get colorList() {
        if (this.isHorizontal) {
            return {
                display: 'grid',
                'grid-template-columns': 'repeat(auto-fit, minmax(140px, 1fr))',
            };
        }
        return { width:  '70%' };
    }

    public get buttonList() {
        if (this.isHorizontal) {
            return {
                display: 'flex',
            };
        }
        return { width: '30%', "min-width": '50px' };
    }

    public get isWithButtons(): boolean {
        return this.buttonsConfiguration?.buttons.length > 0;
    }

    onColorClick(color: IColorCode) {
        if (!this.readOnly) {
            this.internalSelected = color;
            this.colorChanged.emit(this.internalSelected);
        }
    }

    onButtonClick(bntName: LegendButtons) {
        if (!this.readOnly) {
            switch (bntName) {
                case LegendButtons.colorAllBtn:
                    this.buttonClicked.emit(LegendClickEvent.colorAll);
                    break;
                case LegendButtons.colorSelectedBtn:
                    this.buttonClicked.emit(LegendClickEvent.colorSelected);
                    break;
                case LegendButtons.notEvaluatedAllBtn:
                    this.buttonClicked.emit(LegendClickEvent.uncolorAll);
                    break;
                case LegendButtons.notEvaluatedSelectedBtn:
                    this.buttonClicked.emit(LegendClickEvent.uncolorSelected);
                    break;
                default:
                    break;
            }
        }
    }

    private getButtonsConfiguration(): LegendBtnConfig[] {
        return this.buttonsConfiguration?.buttons
            .map((buttonName: LegendButtons) => {
                return this.getButtonCharateristics(buttonName);}
            )
            .sort((a: LegendBtnConfig, b: LegendBtnConfig) => {
                return (a.position - b.position) })
    }

    private getButtonCharateristics(buttonName: LegendButtons): LegendBtnConfig {
        const output: LegendBtnConfig = {
            clickValue: buttonName,
        };
        switch (buttonName) {
            case LegendButtons.colorAllBtn:
                output.color = this.accentButtonColor;
                output.btnType = 'type1';
                output.position = 3;
                output.tooltipDescription = this.actualColor?.description ?? undefined;
                break;
            case LegendButtons.colorSelectedBtn:
                output.color = this.accentButtonColor;
                output.btnType = 'type2';
                output.position = 4;
                output.tooltipDescription = this.actualColor?.description ?? undefined;
                break;
            case LegendButtons.notEvaluatedAllBtn:
                output.color = this.buttonsConfiguration.normalColor?.color ?? undefined;
                output.btnType = 'type1';
                output.position = 1;
                output.tooltipDescription = this.buttonsConfiguration.normalColor?.description ?? undefined;
                break;
            case LegendButtons.notEvaluatedSelectedBtn:
                output.btnType = 'type2';
                output.color = this.buttonsConfiguration.normalColor.color ?? undefined;
                output.position = 2;
                output.tooltipDescription = this.buttonsConfiguration.normalColor?.description ?? undefined
                break;
            default:
                return null;
        }
        return output;
    }
}
